import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import httpClient, { ApiResponse } from '../httpClient';
import { API_ENDPOINTS } from '../endpoints';
import { PaginatedResponse } from '../types';
import { Lead, CreateLeadRequest, UpdateLeadRequest } from '@/models/lead.ts';

// Lead CRUD operations
// Converts flat filter params (including custom_attributes.name=value dot keys)
// into the nested structure the API expects: custom_attributes[name][value]=X
export const buildApiParams = (params: Record<string, any>): Record<string, any> => {
    const result: Record<string, any> = {};
    const customAttrs: Record<string, { value: string }> = {};

    for (const [key, value] of Object.entries(params)) {
        if (key.startsWith('custom_attributes.')) {
            const attrName = key.slice('custom_attributes.'.length);
            customAttrs[attrName] = { value: String(value) };
        } else {
            result[key] = value;
        }
    }

    if (Object.keys(customAttrs).length > 0) {
        result['custom_attributes'] = customAttrs;
    }

    return result;
};

export const useLeads = (params?: {
    page?: number;
    per_page?: number;
    search?: string;
    lead_status?: string;
    lead_source?: string;
    lead_priority?: string;
    assigned_user_id?: string;
    sales_team?: string;
    ib_referral_id?: string;
    country?: string;
    preferred_language?: string;
    time_zone?: string;
    regulatory_region?: string;
    tags?: string;
    instruments_of_interest?: string;
    sort_by?: string;
    sort_direction?: 'asc' | 'desc';
    [key: string]: any;
}) => {
    return useQuery<PaginatedResponse<Lead>>({
        queryKey: ['leads', params],
        queryFn: async () => {
            const { data } = await httpClient.get<PaginatedResponse<Lead>>(
                API_ENDPOINTS.CRM.LEADS,
                { params: params ? buildApiParams(params) : undefined }
            );
            return data;
        },
    });
};

export const useLead = (id: string) => {
    return useQuery<Lead>({
        queryKey: ['leads', id],
        queryFn: async () => {
            const { data } = await httpClient.get<ApiResponse<Lead>>(
                `${API_ENDPOINTS.CRM.LEADS}/${id}`
            );
            return data.data;
        },
        enabled: !!id,
    });
};

export const useCreateLead = () => {
    const queryClient = useQueryClient();

    return useMutation<Lead, Error, CreateLeadRequest>({
        mutationFn: async leadData => {
            const { data } = await httpClient.post<ApiResponse<Lead>>(
                API_ENDPOINTS.CRM.LEADS,
                leadData
            );
            return data.data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['leads'] });
        },
    });
};

export const useUpdateLead = () => {
    const queryClient = useQueryClient();

    return useMutation<Lead, Error, { id: string; data: UpdateLeadRequest }>({
        mutationFn: async ({ id, data }) => {
            const { data: responseData } = await httpClient.put<ApiResponse<Lead>>(
                `${API_ENDPOINTS.CRM.LEADS}/${id}`,
                data
            );
            return responseData.data;
        },
        onSuccess: (_, { id }) => {
            queryClient.invalidateQueries({ queryKey: ['leads'] });
            queryClient.invalidateQueries({ queryKey: ['leads', id] });
        },
    });
};

export const useDeleteLead = () => {
    const queryClient = useQueryClient();

    return useMutation<void, Error, string>({
        mutationFn: async id => {
            await httpClient.delete(`${API_ENDPOINTS.CRM.LEADS}/${id}`);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['leads'] });
        },
    });
};

// Lead navigation based on current filters
export const useLeadNavigation = (currentLeadId: string, filters?: any, sortConfig?: any) => {
    return useQuery<{ next: Lead | null; previous: Lead | null }>({
        queryKey: ['leads', 'navigation', currentLeadId, filters, sortConfig],
        queryFn: async () => {
            // Use the new backend navigation endpoint
            const { data } = await httpClient.get<
                ApiResponse<{ next: Lead | null; previous: Lead | null }>
            >(`${API_ENDPOINTS.CRM.LEADS}/${currentLeadId}/navigation`, {
                params: {
                    ...filters,
                    sort_key: sortConfig?.key,
                    sort_direction: sortConfig?.direction,
                },
            });

            return data.data;
        },
        enabled: !!currentLeadId,
    });
};

export const useConvertLeadToCustomer = () => {
    const queryClient = useQueryClient();

    return useMutation<{ message: string; customer_id: string; customer: any }, Error, string>({
        mutationFn: async (leadId: string) => {
            const { data } = await httpClient.post<
                ApiResponse<{
                    message: string;
                    customer_id: string;
                    customer: any;
                }>
            >(`${API_ENDPOINTS.CRM.LEADS}/${leadId}/convert-to-customer`);
            return data.data;
        },
        onSuccess: (data, leadId) => {
            // Invalidate leads queries
            queryClient.invalidateQueries({ queryKey: ['leads'] });
            queryClient.invalidateQueries({ queryKey: ['leads', leadId] });

            // Invalidate customers queries
            queryClient.invalidateQueries({ queryKey: ['customers'] });
            queryClient.invalidateQueries({ queryKey: ['customers', data.customer_id] });
        },
    });
};

// Bulk update lead status
export const useBulkUpdateLeadStatus = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({ leadIds, statusId }: { leadIds: string[]; statusId: string }) => {
            const { data } = await httpClient.post<ApiResponse>(
                `${API_ENDPOINTS.CRM.LEADS}/bulk-update-status`,
                { lead_ids: leadIds, status_id: statusId }
            );
            return data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['leads'] });
        },
    });
};

// Bulk assign leads
export const useBulkAssignLeads = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({
            leadIds,
            assignedUserId,
        }: {
            leadIds: string[];
            assignedUserId: string | null;
        }) => {
            const { data } = await httpClient.post<ApiResponse>(
                `${API_ENDPOINTS.CRM.LEADS}/bulk-assign`,
                {
                    lead_ids: leadIds,
                    assigned_user_id: assignedUserId ?? null,
                }
            );
            return data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['leads'] });
        },
    });
};

// Bulk delete leads
export const useBulkDeleteLeads = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (leadIds: string[]) => {
            const { data } = await httpClient.post<ApiResponse>(
                `${API_ENDPOINTS.CRM.LEADS}/bulk-delete`,
                {
                    lead_ids: leadIds,
                }
            );
            return data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['leads'] });
        },
    });
};

export interface DuplicateRecord {
    id: string;
    /** Only leads are merged away; a customer can only be the record kept. */
    type: 'lead' | 'customer';
    name: string;
    email: string;
    phone?: string | null;
    additional_phone_number?: string | null;
    status: { id: string; name: string; label: string; color: string } | null;
    assigned_user: { id: string; name: string } | null;
    created_at: string;
    has_financial_records: boolean;
    /** The user may keep this record (edit permission on it). */
    can_keep: boolean;
    /** A lead the user may merge away (edit + delete, no trading records). */
    can_be_merged_in: boolean;
}

export interface DuplicateGroup {
    /** Which rules joined the group: a shared email and/or a shared phone number. */
    matched_by: ('email' | 'phone')[];
    records: DuplicateRecord[];
}

// Groups of duplicate leads/customers the user can merge, largest first
export const useLeadDuplicates = (params: { page: number; per_page?: number }, enabled = true) => {
    return useQuery<PaginatedResponse<DuplicateGroup>>({
        queryKey: ['duplicates', 'list', params],
        queryFn: async () => {
            const { data } = await httpClient.get<PaginatedResponse<DuplicateGroup>>(
                `${API_ENDPOINTS.CRM.LEADS}/duplicates`,
                { params }
            );
            return data;
        },
        enabled,
    });
};

// The duplicate group of one lead or customer (null when it has none)
export const useRecordDuplicates = (recordId: string | undefined) => {
    return useQuery<DuplicateGroup | null>({
        queryKey: ['duplicates', 'record', recordId],
        queryFn: async () => {
            const { data } = await httpClient.get<ApiResponse<{ group: DuplicateGroup | null }>>(
                `${API_ENDPOINTS.CRM.DUPLICATES}/${recordId}`
            );
            return data.data?.group ?? null;
        },
        enabled: !!recordId,
    });
};

// Merge duplicate leads into a lead or a customer (the survivor keeps its values;
// the merged leads' records move to it and they are deleted)
export const useMergeLeads = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({
            survivorId,
            mergedIds,
        }: {
            survivorId: string;
            mergedIds: string[];
        }) => {
            const { data } = await httpClient.post<ApiResponse>(
                `${API_ENDPOINTS.CRM.LEADS}/merge`,
                { survivor_id: survivorId, merged_ids: mergedIds }
            );
            return data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['duplicates'] });
            queryClient.invalidateQueries({ queryKey: ['leads'] });
            queryClient.invalidateQueries({ queryKey: ['customers'] });
            queryClient.invalidateQueries({ queryKey: ['notes'] });
        },
    });
};

// Bulk convert leads to customers
export const useBulkConvertLeadsToCustomers = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (leadIds: string[]) => {
            const { data } = await httpClient.post<ApiResponse>(
                `${API_ENDPOINTS.CRM.LEADS}/bulk-convert-to-customer`,
                { lead_ids: leadIds }
            );
            return data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['leads'] });
            queryClient.invalidateQueries({ queryKey: ['customers'] });
        },
    });
};
